import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { DateInput, TextInput } from '../inputs';
import { LineItemSection } from './LineItemSection';
import { NameEntry, LineItemDetail } from '../../types/asn';

interface OrderSectionProps {
  order: {
    orderNumber: string;
    orderDate: string;
    names: NameEntry[];
    details: LineItemDetail[];
  };
  orderIndex: number;
  onChange: (orderIndex: number, field: string, value: any) => void;
  onRemove: (orderIndex: number) => void;
  onAddLineItem: (orderIndex: number) => void;
  onRemoveLineItem: (orderIndex: number, lineItemIndex: number) => void;
  onLineItemChange: (orderIndex: number, lineItemIndex: number, field: string, value: any) => void;
  errors?: Record<string, string>;
}

export function OrderSection({
  order,
  orderIndex,
  onChange,
  onRemove,
  onAddLineItem,
  onRemoveLineItem,
  onLineItemChange,
  errors = {},
}: OrderSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  // WDEV-142 (Natalie): Shipping Names & Locations should be expanded by
  // default so the operator sees the seeded Ship-From/Ship-To identity without
  // having to click into the panel first.
  const [namesExpanded, setNamesExpanded] = useState(true);

  const nameTypeOptions = [
    { value: 'SF', label: 'Ship From' },
    { value: 'ST', label: 'Ship To' },
    { value: 'ZZ', label: 'Additional Location' },
  ];

  const qualifierOptions = [
    { value: '92', label: 'Buyer Assigned Code' },
    { value: '91', label: 'Seller Assigned Code' },
    { value: '16', label: 'Zipcode' },
    { value: '1', label: 'D-U-N-S Number' },
  ];

  const handleAddName = () => {
    const newNames = [...order.names, { type: 'SF', name: '', qualifier: '92', id: '' }];
    onChange(orderIndex, 'names', newNames);
  };

  const handleRemoveName = (nameIndex: number) => {
    const newNames = order.names.filter((_, i) => i !== nameIndex);
    onChange(orderIndex, 'names', newNames);
  };

  const handleNameChange = (nameIndex: number, field: keyof NameEntry, value: string) => {
    const newNames = [...order.names];
    newNames[nameIndex] = { ...newNames[nameIndex], [field]: value };
    onChange(orderIndex, 'names', newNames);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-blue-50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-blue-600 hover:text-blue-700"
          >
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          <h4 className="font-semibold text-gray-900">
            Order #{orderIndex + 1}: {order.orderNumber || 'New Order'}
          </h4>
        </div>
        <button
          type="button"
          onClick={() => onRemove(orderIndex)}
          className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50"
        >
          <Trash2 size={18} />
        </button>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextInput
              label="Order Number"
              value={order.orderNumber}
              onChange={(value) => onChange(orderIndex, 'orderNumber', value)}
              name={`orders[${orderIndex}].orderNumber`}
              required
              error={errors[`order_${orderIndex}_orderNumber`]}
              placeholder="e.g., 5550146575"
              helperText="PO Number"
            />
            <DateInput
              label="Order Date"
              value={order.orderDate}
              onChange={(value) => onChange(orderIndex, 'orderDate', value)}
              name={`orders[${orderIndex}].orderDate`}
              required
              error={errors[`order_${orderIndex}_orderDate`]}
              helperText="Purchase order date"
            />
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={() => setNamesExpanded(!namesExpanded)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                {namesExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                Shipping Names & Locations ({order.names.length})
              </button>
              <button
                type="button"
                onClick={handleAddName}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded hover:bg-green-100"
              >
                <Plus size={16} />
                Add Name
              </button>
            </div>

            {namesExpanded && (
              <div className="space-y-3">
                {order.names.map((name, nameIndex) => (
                  <div key={nameIndex} className="p-3 bg-gray-50 rounded border border-gray-200 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className="text-xs font-medium text-gray-700 mb-1 block">Type</label>
                        <select
                          value={name.type}
                          onChange={(e) => handleNameChange(nameIndex, 'type', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        >
                          {nameTypeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700 mb-1 block">Name</label>
                        <input
                          type="text"
                          value={name.name}
                          onChange={(e) => handleNameChange(nameIndex, 'name', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          placeholder="Location name"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700 mb-1 block">ID Type</label>
                        <select
                          value={name.qualifier}
                          onChange={(e) => handleNameChange(nameIndex, 'qualifier', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        >
                          {qualifierOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="text-xs font-medium text-gray-700 mb-1 block">ID</label>
                          <input
                            type="text"
                            value={name.id}
                            onChange={(e) => handleNameChange(nameIndex, 'id', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            placeholder="Location ID"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveName(nameIndex)}
                          className="mt-5 text-red-600 hover:text-red-700 p-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    {/* WDEV-51: optional address block for SF / ST / BT / SE.
                        Pre-filled from the TP template for 1621's SF; user can still edit. */}
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                      <div className="md:col-span-2">
                        <label className="text-xs font-medium text-gray-700 mb-1 block">Address 1</label>
                        <input
                          type="text"
                          value={name.address1 ?? ''}
                          onChange={(e) => handleNameChange(nameIndex, 'address1', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          placeholder="Street address"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs font-medium text-gray-700 mb-1 block">Address 2</label>
                        <input
                          type="text"
                          value={name.address2 ?? ''}
                          onChange={(e) => handleNameChange(nameIndex, 'address2', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          placeholder="Suite / unit (optional)"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700 mb-1 block">City</label>
                        <input
                          type="text"
                          value={name.city ?? ''}
                          onChange={(e) => handleNameChange(nameIndex, 'city', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs font-medium text-gray-700 mb-1 block">St</label>
                          <input
                            type="text"
                            value={name.state ?? ''}
                            onChange={(e) => handleNameChange(nameIndex, 'state', e.target.value)}
                            maxLength={2}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs font-medium text-gray-700 mb-1 block">Zip</label>
                          <input
                            type="text"
                            value={name.postalCode ?? ''}
                            onChange={(e) => handleNameChange(nameIndex, 'postalCode', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {order.names.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-2">No names added. Click "Add Name" to add shipping locations.</p>
                )}
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <h5 className="font-medium text-gray-900">Line Items ({order.details.length})</h5>
              <button
                type="button"
                onClick={() => onAddLineItem(orderIndex)}
                className="flex items-center px-3 py-1.5 text-sm gap-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus size={18} />
                Add Line Item
              </button>
            </div>

            <div className="space-y-4">
              {order.details.map((detail, lineItemIndex) => (
                <LineItemSection
                  key={lineItemIndex}
                  lineItem={detail}
                  lineItemIndex={lineItemIndex}
                  orderIndex={orderIndex}
                  onChange={onLineItemChange}
                  onRemove={onRemoveLineItem}
                  errors={errors}
                />
              ))}
              {order.details.length === 0 && (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-500 mb-2">No line items added</p>
                  <button
                    type="button"
                    onClick={() => onAddLineItem(orderIndex)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Add your first line item
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
